/**
 * The setup wizard is shared with the box office (tkt-new-ui), which is built on shadcn + sonner.
 * These map its few dependencies onto this console's own Button, toast bus and printer store, so
 * the wizard file itself stays a straight copy — one behaviour, verified once, in two apps.
 */
import React, { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button as ConsoleButton } from '../../../ui/Button';
import { toast as busToast } from '../../../lib/buses';
import { usePrinter, connect, disconnect, saveProfile, masungPrinter } from '../../../lib/ticketPrinter/printerStore';
import type { MasungTicketData } from '../../../lib/masung-printer/printer';

export const cn = (...xs: unknown[]) => xs.flat().filter(Boolean).join(' ');

export const toast = Object.assign((m: string) => busToast(m, 'info'), {
  success: (m: string) => busToast(m, 'success'),
  info: (m: string) => busToast(m, 'info'),
  error: (m: string) => busToast(m, 'error'),
});

const VARIANT: Record<string, string> = { default: 'primary', outline: 'secondary', ghost: 'ghost' };
export const Button = ({ variant = 'default', size, className = '', ...rest }: any) => (
  <ConsoleButton variant={VARIANT[variant] ?? 'secondary'} size={size === 'sm' ? 'sm' : 'md'} className={className} {...rest} />
);

const DialogCtx = React.createContext<{ onOpenChange: (o: boolean) => void }>({ onOpenChange: () => {} });
export const Dialog = ({ open, onOpenChange, children }: { open: boolean; onOpenChange: (o: boolean) => void; children: ReactNode }) =>
  open ? createPortal(
    <DialogCtx.Provider value={{ onOpenChange }}>
      <div className="fixed inset-0 z-[60] bg-black/30 flex items-center justify-center" onClick={() => onOpenChange(false)}>{children}</div>
    </DialogCtx.Provider>, document.body) : null;
export const DialogContent = ({ className = '', children }: { className?: string; children: ReactNode }) => {
  const { onOpenChange } = React.useContext(DialogCtx);
  return (
    <div className={`relative bg-white rounded-xl border border-[#E6E4E1] shadow-2xl ${className}`} onClick={(e) => e.stopPropagation()}>
      <button aria-label="Close" onClick={() => onOpenChange(false)} className="absolute top-3 right-3 w-7 h-7 rounded hover:bg-[#F2F1ED] text-[#6B7280]">✕</button>
      {children}
    </div>
  );
};
export const DialogTitle = ({ className = '', children }: any) => <div className={`font-semibold text-[#1F2937] ${className}`}>{children}</div>;
export const DialogDescription = ({ className = '', children }: any) => <div className={`text-[#6B7280] ${className}`}>{children}</div>;

/** The box office hook's shape, served by this console's single printer store. */
export function useMasungPrinter() {
  const p = usePrinter();
  return {
    status: { connected: p.connected, deviceName: p.deviceName },
    profile: p.profile,
    saveProfile,
    printer: masungPrinter,
    connect: async () => { const f = await connect(); if (f) throw new Error(f); },
    disconnect,
    beginPrintJob: () => masungPrinter.beginPrintJob(),
    printTicket: (t: MasungTicketData) => masungPrinter.printTicket({ ...t, mode: p.profile?.paperType === 'continuous' ? 'cut' : 'tear', showHeader: p.profile?.showHeader ?? true }),
  };
}
